import { useClientSide } from '@carvana/merch-react-hooks';
import dynamic from 'next/dynamic';
import { memo, useContext } from 'react';
import { withErrorBoundary } from '@/srp/ErrorBoundary';
import useLoadSaveSearchNudge from '@/srp/Modals/SaveSearchNudge/hooks/useLoadSaveSearchNudge';
import { useDeviceType, useIsNative } from '~/context/common/DeviceContext';
import { useIsLoggedIn } from '~/context/common/UserContext';
import ModalContext from '~/context/srp/ModalContext';

const ActivationModal = dynamic(
  () =>
    import(
      /* webpackChunkName: "ActivationModal" */ '@/srp/Modals/ActivationModal'
    ),
  { ssr: false }
);

const CreateSavedSearchModal = dynamic(
  () =>
    import(
      /* webpackChunkName: "CreateSavedSearchModal" */ '@/srp/Modals/SearchAlert/CreateSavedSearchModal'
    ),
  { ssr: false }
);

const EvTaxCreditModal = dynamic(
  () =>
    import(
      /* webpackChunkName: "EvTaxCreditModal" */ '@/srp/Modals/EvTaxCreditModal'
    ),
  { ssr: false }
);

const FilterModal = dynamic(
  () =>
    import(
      /* webpackChunkName: "FilterModal" */ '@/srp/MainLayout/MobileFilters/FilterModal/FilterModalWrapper'
    ),
  { ssr: false }
);

const GeolocationModal = dynamic(
  () =>
    import(
      /* webpackChunkName: "GeolocationModal" */ '@/common/Modals/GeolocationModal'
    ),
  { ssr: false }
);

const ManageSavedSearchesModal = dynamic(
  () =>
    import(
      /* webpackChunkName: "ManageSavedSearchesModal" */ '@/srp/Modals/SearchAlert/ManageSavedSearchesModal'
    ),
  { ssr: false }
);

const MoreDetailsModal = dynamic(
  () =>
    import(
      /* webpackChunkName: "MoreDetailsModal" */ '@/srp/Modals/MoreDetailsModal'
    ),
  { ssr: false }
);

const SaveSearchModal = dynamic(
  () =>
    import(
      /* webpackChunkName: "SaveSearchModal" */ '@/srp/Modals/SaveSearchModal'
    ),
  { ssr: false }
);

const SaveSearchNudge = dynamic(
  () =>
    import(
      /* webpackChunkName: "SaveSearchNudge" */ '@/srp/Modals/SaveSearchNudge'
    ),
  { ssr: false }
);

const SoftPullMfeModal = dynamic(
  () =>
    import(
      /* webpackChunkName: "SoftPullMfeModal" */ '@/srp/Modals/SoftPullMfeModal'
    ),
  { ssr: false }
);

const WIPNotificationsModal = dynamic(
  () =>
    import(
      /* webpackChunkName: "WIPNotificationsModal" */ '@/srp/Modals/WIPNotifications'
    )
);

type OwnProps = {
  loadActivationModal: boolean;
  loadAuthedModals: boolean;
  loadFilterModal: boolean;
  loadGeolocationModal: boolean;
  loadMoreDetailsModal: boolean;
  loadSaveSearchNudge: boolean;
  loadSoftPullModal: boolean;
};

const ModalsContent = memo(
  ({
    loadActivationModal,
    loadAuthedModals,
    loadFilterModal,
    loadGeolocationModal,
    loadMoreDetailsModal,
    loadSaveSearchNudge,
    loadSoftPullModal
  }: OwnProps) => {
    const { isGeolocationModalOpen, setGeolocationModalOpen } =
      useContext(ModalContext);

    return (
      <>
        {loadActivationModal && <ActivationModal />}
        {loadAuthedModals && (
          <>
            <CreateSavedSearchModal />
            <ManageSavedSearchesModal />
            <SaveSearchModal />
            <WIPNotificationsModal />
          </>
        )}
        <EvTaxCreditModal />
        {loadFilterModal && <FilterModal />}
        {loadGeolocationModal && (
          <GeolocationModal
            isGeolocationModalOpen={isGeolocationModalOpen}
            setGeolocationModalOpen={setGeolocationModalOpen}
          />
        )}
        {loadMoreDetailsModal && <MoreDetailsModal />}
        {loadSaveSearchNudge && <SaveSearchNudge />}
        {loadSoftPullModal && <SoftPullMfeModal />}
      </>
    );
  }
);

function ModalsState() {
  const isLoggedIn = useIsLoggedIn();
  const isClientSide = useClientSide();
  const isNative = useIsNative();
  const { isDesktop } = useDeviceType();
  const loadSaveSearchNudge = useLoadSaveSearchNudge();

  return (
    <ModalsContent
      loadActivationModal
      loadAuthedModals={isLoggedIn}
      loadFilterModal={!isDesktop}
      loadGeolocationModal={isClientSide && !isNative}
      loadMoreDetailsModal
      loadSaveSearchNudge={loadSaveSearchNudge}
      loadSoftPullModal={!isNative}
    />
  );
}

export default withErrorBoundary(ModalsState);
